import { supabase } from '../supabaseClient'
import {
  RangoBrazo, ModificadoresBrazo,
  RangoAntebrazo, ModificadorAntebrazo,
  RangoMuneca, ModificadorMuneca,
  RangoGiroMuneca,
  RangoCuello, ModificadoresCuello,
  RangoTronco, ModificadoresTronco,
  CondicionPiernas,
  TipoActividad, CargaFuerza,
  NivelActuacionRula,
} from './rula'

// ===== Evaluaciones RULA por local/puesto de trabajo (multi-empresa) =====
// Mismo patrón que gestion-tecnica-ruido.ts / noise_calculations: inputs y
// resultado se guardan como jsonb libre, tipados aquí para que el resto del
// código (formulario, PDF) no maneje `any`.

export interface RulaAssessmentInputs {
  rangoBrazo: RangoBrazo
  modBrazo: ModificadoresBrazo
  rangoAntebrazo: RangoAntebrazo
  modAntebrazo: ModificadorAntebrazo
  rangoMuneca: RangoMuneca
  modMuneca: ModificadorMuneca
  rangoGiroMuneca: RangoGiroMuneca
  rangoCuello: RangoCuello
  modCuello: ModificadoresCuello
  rangoTronco: RangoTronco
  modTronco: ModificadoresTronco
  condicionPiernas: CondicionPiernas
  tipoActividad: TipoActividad
  cargaFuerza: CargaFuerza
}

// Resultado calculado en RulaMethodologyPage.tsx a partir de src/lib/rula.ts
// — mismo criterio que NoiseCalculationResultado: no existe un
// calculateFullReport() único, esta forma es el contrato de persistencia
// que agrupa las puntuaciones intermedias y finales para guardar/mostrar/imprimir.
export interface RulaAssessmentResultado {
  puntuacionBrazo: number
  puntuacionAntebrazo: number
  puntuacionMuneca: number
  puntuacionGiroMuneca: number
  grupoA: number
  puntuacionCuello: number
  puntuacionTronco: number
  puntuacionPiernas: number
  grupoB: number
  puntuacionC: number
  puntuacionD: number
  puntuacionFinal: number
  nivelActuacion: NivelActuacionRula
}

export interface RulaAssessmentRow {
  id: string
  company_id: string
  job_position_id: string | null
  descripcion: string
  lado: 'izquierdo' | 'derecho'
  inputs: RulaAssessmentInputs
  resultado: RulaAssessmentResultado
  created_by: string | null
  created_at: string
  job_position_name: string | null
}

export interface CreateRulaAssessmentInput {
  companyId: string
  jobPositionId?: string
  descripcion: string
  lado: 'izquierdo' | 'derecho'
  inputs: RulaAssessmentInputs
  resultado: RulaAssessmentResultado
  createdBy: string | null
}

export async function createRulaAssessment(input: CreateRulaAssessmentInput): Promise<void> {
  const { error } = await supabase.from('rula_assessments').insert({
    company_id: input.companyId,
    job_position_id: input.jobPositionId ?? null,
    descripcion: input.descripcion,
    lado: input.lado,
    inputs: input.inputs,
    resultado: input.resultado,
    created_by: input.createdBy,
  })
  if (error) throw error
}

export async function fetchRulaAssessments(companyId: string): Promise<RulaAssessmentRow[]> {
  if (!companyId) return []
  const { data, error } = await supabase
    .from('rula_assessments')
    .select('id, company_id, job_position_id, descripcion, lado, inputs, resultado, created_by, created_at, job_positions(name)')
    .eq('company_id', companyId)
    .order('created_at', { ascending: false })
  if (error) throw error
  return (data ?? []).map((r: any) => ({
    id: r.id,
    company_id: r.company_id,
    job_position_id: r.job_position_id,
    descripcion: r.descripcion,
    lado: r.lado,
    inputs: r.inputs,
    resultado: r.resultado,
    created_by: r.created_by,
    created_at: r.created_at,
    job_position_name: r.job_positions?.name ?? null,
  })) as RulaAssessmentRow[]
}

export async function updateRulaAssessment(id: string, input: CreateRulaAssessmentInput): Promise<void> {
  const { error } = await supabase.from('rula_assessments').update({
    descripcion: input.descripcion,
    lado: input.lado,
    job_position_id: input.jobPositionId ?? null,
    inputs: input.inputs,
    resultado: input.resultado,
  }).eq('id', id)
  if (error) throw error
}

export async function deleteRulaAssessment(id: string): Promise<void> {
  const { error } = await supabase.from('rula_assessments').delete().eq('id', id)
  if (error) throw error
}
